"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ArrowRight, Sparkles, ShieldCheck, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
interface PricingPlan {
  id: string;
  name: string;
  subtitle: string;
  target: string;
  price: string;
  billing: string;
  popular: boolean;
  badge?: string;
  features: string[];
  ctaLabel: string;
}
const plans: PricingPlan[] = [{
  id: "audit",
  name: "AI Search Audit",
  subtitle: "Getting Started",
  target: "For businesses wanting clarity on how AI engines currently discover and cite their brand.",
  price: "£497",
  billing: "Starting from / one-time investment",
  popular: false,
  features: ["AI visibility assessment across ChatGPT, Google AI Mode, Perplexity, Claude, and Copilot", "Analysis of current brand mentions and citations in AI responses", "Entity footprint evaluation and structured data review", "Competitor AI visibility benchmarking", "Prioritised action plan with clear technical and content recommendations"],
  ctaLabel: "Request an AI Audit"
}, {
  id: "implementation",
  name: "Complete AI Optimisation",
  subtitle: "Full Implementation",
  target: "For businesses ready to execute a full strategy to improve AI citation readiness.",
  price: "£997",
  billing: "Starting from / month (3 or 6-month terms)",
  popular: true,
  badge: "Most popular",
  features: ["Everything included in the AI Search Audit", "Content optimisation structured specifically for AI model extraction", "Advanced schema markup and entity graph implementation", "Technical improvements for AI crawler accessibility and indexing", "Entity expansion across trusted citation sources and knowledge nodes", "Monthly AI visibility tracking, citation checks, and direct progress reviews"],
  ctaLabel: "Discuss Implementation"
}, {
  id: "consultation",
  name: "AI Search Consultation",
  subtitle: "Ongoing Partnership",
  target: "For established businesses needing bespoke ongoing guidance as AI search models evolve.",
  price: "Bespoke",
  billing: "Tailored monthly advisory retainer",
  popular: false,
  features: ["Continuous monitoring of brand visibility across emerging AI search tools", "Strategic roadmap updates responding to major AI search algorithm shifts", "Ongoing authority building and brand co-occurrence recommendations", "Quarterly visibility reports with deep competitive benchmarking", "Direct, priority access to me via private communication channel"],
  ctaLabel: "Enquire About Advisory"
}];
export default function Pricing() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="aisearchpricing" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium tracking-wide uppercase mb-4">
            <Sparkles className="size-3.5 text-accent" />
            <span>Direct Freelance Consultancy</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground mb-5">
            AI search optimisation investment options
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            I do not offer generic or one-size-fits-all packages. Every project begins with understanding your specific market position, technical foundation, and real search landscape. If you also need a broader organic review, explore my comprehensive{" "}
            <Link to="/SeoAuditsPage" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
              SEO audit
            </Link>{" "}
            before starting.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {plans.map((plan, index) => {
          const isHighlighted = plan.popular;
          return <motion.div key={plan.id} data-index={index} variants={cardVariants} whileHover={shouldReduceMotion ? {} : {
            y: -4
          }} transition={{
            duration: 0.2
          }} className="flex flex-col h-full">
                <Card className={`relative flex flex-col justify-between h-full rounded-xl border transition-[border-color,box-shadow] duration-200 ${isHighlighted ? "border-primary shadow-lg bg-card ring-1 ring-primary/20" : "border-border shadow-sm hover:border-primary/40 bg-card"}`}>
                  {isHighlighted && plan.badge && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground font-medium px-3 py-1 text-xs shadow-xs rounded-full">
                        {plan.badge}
                      </Badge>
                    </div>}

                  <CardHeader className="pt-6 pb-4">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="text-xl md:text-2xl font-serif font-semibold text-card-foreground">
                        {plan.name}
                      </CardTitle>
                      <p className="text-xs font-semibold uppercase tracking-wider text-accent font-sans">
                        {plan.subtitle}
                      </p>
                    </div>

                    <CardDescription className="text-sm text-muted-foreground mt-2 min-h-[40px] leading-snug">
                      {plan.target}
                    </CardDescription>

                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-serif">
                          {plan.price}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 font-medium">
                        {plan.billing}
                      </p>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 py-4">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 font-sans">
                      What is included:
                    </div>
                    <ul className="space-y-3">
                      {plan.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
                          <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Check className="size-2.5 stroke-[3]" />
                          </span>
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-4 pb-6">
                    <Button asChild variant={isHighlighted ? "default" : "outline"} className={`w-full font-medium h-11 text-sm rounded-lg ${isHighlighted ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border-border text-foreground hover:bg-accent hover:text-accent-foreground"}`}>
                      <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                        <span>{plan.ctaLabel}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Realistic Expectations Notice */}
        <div className="mt-12 p-4 md:p-6 rounded-xl border border-border bg-card/60 max-w-4xl mx-auto text-center flex flex-col md:flex-row items-center justify-center gap-3">
          <ShieldCheck className="size-5 text-primary shrink-0" />
          <p className="text-xs md:text-sm text-muted-foreground text-left md:text-center leading-relaxed">
            <strong className="text-foreground font-semibold">Honest approach:</strong> AI search models constantly update their knowledge sources. I do not promise guaranteed citations or rankings. My work focuses on building verified technical authority, clear entity signals, and citation readiness to give your brand the strongest possible visibility.
          </p>
        </div>

        {/* Bottom CTA Block */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 font-sans">
            <HelpCircle className="size-3.5" />
            <span>Unsure which option matches your stage?</span>
          </div>
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4">
            Let us review your current AI search footprint
          </h3>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            Book a direct, no-obligation consultation with me. We will inspect how your brand currently appears in generative search results and discuss the right next step.
          </p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 py-3 text-base shadow-sm">
            <Link to="/ContactPage">
              Book a Free SEO Consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}